import { IconCheck } from '@tabler/icons-react'
import classNames from 'classnames'
import Link from 'next/link'
import {
  DeliveryStatus,
  DeliveryType,
  OrderDeliveryCardProps,
} from 'shared-types'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import styles from './OrderDeliveryCard.module.css'

const OrderDeliveryCard = ({
  productsCount,
  orderNumber,
  deliveryType,
  predictedDeliveryAtRange,
  status,
}: OrderDeliveryCardProps) => {
  const isSelectedDelivery = deliveryType !== DeliveryType.PICKUP

  const stepSize = isSelectedDelivery ? Number('25') : Number('33.33')
  const steps = [
    { label: 'Order Placed', status: DeliveryStatus.Placed },
    { label: 'Processing Order', status: DeliveryStatus.Processing },
    ...(isSelectedDelivery
      ? [
          { label: 'In Transit', status: DeliveryStatus.InTransit },
          { label: 'Out for Delivery', status: DeliveryStatus.OutForDelivery },
          { label: 'Delivered', status: DeliveryStatus.Delivered },
        ]
      : [
          { label: 'Ready to Collect', status: DeliveryStatus.ReadyToCollect },
          { label: 'Collected', status: DeliveryStatus.Delivered },
        ]),
  ]

  const statusIdx = steps.reduce((reducer, step, index) => {
    return status === step.status ? index : reducer
  }, 0)
  const progress = `${statusIdx * stepSize}%`

  return (
    <div
      className={classNames(
        styles.root,
        'border border-primary-border rounded-md px-2 py-4 md:px-4 md:py-6'
      )}>
      <Link href={`/account/orders/${orderNumber}`}>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodyRegularBold}>
          Order #{orderNumber}
        </Typography>

        <div className={classNames('mt-1 h-4', styles.summary)}>
          {predictedDeliveryAtRange?.from && predictedDeliveryAtRange?.to && (
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyRegular}>
              {productsCount} Items - delivers{' '}
              {new Date(predictedDeliveryAtRange?.from).toLocaleDateString(
                'en-US',
                {
                  month: 'long',
                  day: 'numeric',
                }
              )}{' '}
              -{' '}
              {new Date(predictedDeliveryAtRange?.to).toLocaleDateString(
                'en-US',
                {
                  month: 'long',
                  day: 'numeric',
                }
              )}
            </Typography>
          )}
        </div>
        <div
          className={classNames(
            styles.progress,
            'relative  mt-1.5 mx-3 pt-2.5'
          )}>
          <div
            className='absolute top-2.5 left-0 w-full bg-state-success-light rounded-full pb-1'
            style={{ width: progress }}
          />
          {steps.map((step, index) => {
            const isActived = index <= statusIdx
            const isLastStep = index === steps.length - 1
            return (
              <div
                key={index}
                className={classNames(
                  'absolute top-0 w-6 flex flex-col -translate-x-1/2',
                  {
                    'items-start text-left': index === 0,
                    'items-end text-right': isLastStep,
                    'items-center text-center': index > 0 && !isLastStep,
                  }
                )}
                style={{ left: `${Number(index) * stepSize}%` }}>
                <div
                  className={classNames(
                    isActived ? 'opacity-1' : 'opacity-0',
                    'flex items-center justify-center w-6 h-6',
                    'bg-state-success-light text-white border-2 border-white rounded-full'
                  )}>
                  <IconCheck size={14} />
                </div>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodyRegularBold}
                  className={classNames('mt-1.5', styles.cardText, {
                    [styles.inactiveStep]: !isActived,
                  })}>
                  {step.label}
                </Typography>
              </div>
            )
          })}
        </div>
      </Link>
    </div>
  )
}

export default OrderDeliveryCard
